/**
 * Email signup popup — "A fifth card, on us." (sections/spindle-signup.liquid)
 *
 * Decides when the panel appears and remembers what the visitor chose. The
 * offer itself lives in Shopify: the form is Shopify's customer form, which
 * tags the customer `bonus-card`, and the card builder reads that tag when
 * their next Spindle order arrives. Nothing here grants anything.
 *
 * When it opens on its own (at most once per page view):
 * - Exit intent on desktop: the pointer leaves through the top of the
 *   window, after a short grace period. Any page that renders the section.
 * - Time on page: 10 seconds, the middle of the 5-15 s range popup
 *   benchmarks favor. Not on product pages, where it would cover the buy
 *   box.
 * - Phones and tablets: the second page view of a visit, after a few seconds.
 *   Not on product pages either.
 * It waits while a drawer or dialog is open or the visitor is typing.
 *
 * What it remembers (localStorage, per browser):
 * - subscribed: never again.
 * - closed (×): no auto-open for 14 days; a tab in the corner reopens it.
 * - declined ("No thanks"): nothing for 14 days, no tab.
 * Visitors arriving from an email link (utm_medium=email, or Klaviyo's _kx)
 * are already on the list, so it stays closed for the rest of that visit.
 */

const STATE_KEY = 'spindle:signup';
const VISIT_KEY = 'spindle:signup:visit';
const PENDING_KEY = 'spindle:signup:pending';

const QUIET_MS = 14 * 24 * 60 * 60 * 1000;
const GRACE_MS = 5000;
const DWELL_MS = 10000;
const SECOND_VIEW_DELAY_MS = 6000;
const BUSY_RETRY_MS = 4000;


/** @returns {{ status: 'subscribed' | 'closed' | 'declined', at: number } | null} */
function readState() {
  try {
    const state = JSON.parse(localStorage.getItem(STATE_KEY) ?? 'null');
    if (!state || typeof state.status !== 'string') return null;
    if (state.status === 'subscribed') return state;
    // A closed or declined popup earns another chance after the quiet period.
    if (typeof state.at === 'number' && Date.now() - state.at < QUIET_MS) return state;
  } catch {
    // Storage blocked or corrupt: treat as a first visit.
  }
  return null;
}

/** @param {'subscribed' | 'closed' | 'declined'} status */
function writeState(status) {
  try {
    localStorage.setItem(STATE_KEY, JSON.stringify({ status, at: Date.now() }));
  } catch {
    // Without storage the popup simply forgets; nothing else depends on it.
  }
}

/** Counts page views in this visit and notes arrivals from our emails. */
function trackVisit() {
  let visit = { views: 0, fromEmail: false };
  try {
    visit = { ...visit, ...JSON.parse(sessionStorage.getItem(VISIT_KEY) ?? '{}') };
  } catch {
    // Fall through with a fresh visit.
  }

  const params = new URLSearchParams(location.search);
  if (params.get('utm_medium')?.toLowerCase() === 'email' || params.has('_kx')) {
    visit.fromEmail = true;
  }
  visit.views += 1;

  try {
    sessionStorage.setItem(VISIT_KEY, JSON.stringify(visit));
  } catch {
    // Same as above.
  }
  return visit;
}

/** True while something else has the visitor's attention. */
function visitorIsBusy() {
  // Open dialogs cover the cart drawer, search and quick add; the details
  // elements are the Spindle header's mobile menu and Horizon's drawer.
  if (document.querySelector('dialog[open], details.m-menu[open], details.menu-drawer-container[open]')) {
    return true;
  }
  const active = document.activeElement;
  return active instanceof HTMLElement && active.matches('input, textarea, select, [contenteditable="true"]');
}

class SpindleSignup extends HTMLElement {
  #lifetime = new AbortController();
  #triggers = new AbortController();
  /** @type {number[]} */
  #timers = [];
  #opened = false;
  #waiting = false;
  #subscribed = false;

  connectedCallback() {
    this.panel = /** @type {HTMLElement} */ (this.querySelector('[data-panel]'));
    this.form = /** @type {HTMLFormElement} */ (this.querySelector('form'));
    this.emailInput = /** @type {HTMLInputElement} */ (this.querySelector('[data-email-input]'));
    this.submitButton = /** @type {HTMLButtonElement} */ (this.querySelector('[data-submit]'));
    this.errorMessage = /** @type {HTMLElement} */ (this.querySelector('[data-error]'));
    this.tab = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-tab]'));

    const { signal } = this.#lifetime;
    for (const button of this.querySelectorAll('[data-close]')) {
      button.addEventListener('click', () => this.#close(), { signal });
    }
    this.querySelector('[data-decline]')?.addEventListener('click', () => this.#decline(), { signal });
    this.tab?.addEventListener('click', () => this.#open({ focus: true }), { signal });
    this.form.addEventListener('submit', this.#submit, { signal });
    this.panel.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape') this.#close();
      },
      { signal }
    );

    if (this.hasAttribute('data-design-mode')) {
      this.#previewInEditor();
      return;
    }

    const visit = trackVisit();
    if (this.#resumeAfterSubmit()) return;

    const state = readState();
    if (state?.status === 'subscribed' || state?.status === 'declined') return;
    if (state?.status === 'closed') {
      this.#showTab();
      return;
    }
    if (visit.fromEmail) return;

    this.#arm(visit);
  }

  disconnectedCallback() {
    this.#lifetime.abort();
    this.#triggers.abort();
    for (const timer of this.#timers) clearTimeout(timer);
  }

  /** @param {{ views: number }} visit */
  #arm(visit) {
    const { signal } = this.#triggers;
    const start = performance.now();
    const productPage = this.dataset.pageType === 'product';
    const finePointer = matchMedia('(hover: hover) and (pointer: fine)').matches;

    if (finePointer) {
      document.addEventListener(
        'mouseout',
        (event) => {
          if (event.relatedTarget || event.clientY > 0) return;
          if (performance.now() - start < GRACE_MS) return;
          this.#autoOpen();
        },
        { signal }
      );
    }

    if (productPage) return;

    this.#later(() => this.#autoOpen(), DWELL_MS);

    if (!finePointer && visit.views >= 2) {
      this.#later(() => this.#autoOpen(), SECOND_VIEW_DELAY_MS);
    }
  }

  #autoOpen() {
    if (this.#opened || this.#waiting) return;
    if (visitorIsBusy()) {
      // One retry at a time, however many triggers fire in the meantime.
      this.#waiting = true;
      this.#later(() => {
        this.#waiting = false;
        this.#autoOpen();
      }, BUSY_RETRY_MS);
      return;
    }
    this.#open({ focus: false });
  }

  /**
   * Opening on its own never moves focus: the visitor didn't ask for it.
   * Opening from the tab does, straight to the email field.
   * @param {{ focus: boolean }} options
   */
  #open({ focus }) {
    this.#opened = true;
    this.#triggers.abort();
    if (this.tab) this.tab.hidden = true;

    this.panel.hidden = false;
    // Let the browser lay out the hidden state first so the slide-in runs.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.panel.dataset.open = '';
      });
    });

    if (focus) this.emailInput.focus({ preventScroll: true });
  }

  #hide() {
    delete this.panel.dataset.open;
    this.panel.hidden = true;
  }

  #close() {
    const hadFocus = this.panel.contains(document.activeElement);
    this.#hide();
    if (this.hasAttribute('data-design-mode')) {
      this.#showTab();
      return;
    }
    if (this.#subscribed) return;

    writeState('closed');
    this.#showTab();
    if (hadFocus) this.tab?.focus({ preventScroll: true });
  }

  #decline() {
    writeState('declined');
    this.#hide();
    if (this.tab) this.tab.hidden = true;
  }

  #showTab() {
    if (this.tab) this.tab.hidden = false;
  }

  /**
   * The form posts the ordinary way, as a full page load. Shopify's spam
   * protection (hCaptcha) binds itself to customer forms and submits them
   * once it has a token, so submitting through fetch would race it. Remember
   * the email for the round trip; #resumeAfterSubmit reopens the panel when
   * Shopify sends the visitor back.
   */
  #submit = () => {
    try {
      sessionStorage.setItem(PENDING_KEY, this.emailInput.value.trim());
    } catch {
      // The signup still goes through; only the confirmation is lost.
    }
    this.submitButton.setAttribute('aria-busy', 'true');
    // If the spam check never resolves, don't leave the button stuck.
    this.#later(() => this.submitButton.removeAttribute('aria-busy'), 10000);
  };

  /**
   * After the form posts, Shopify returns the visitor with
   * ?customer_posted=true (possibly by way of its spam challenge page), or
   * renders the page with the form's errors. Either way, reopen the panel
   * where they left off.
   * @returns {boolean} whether a pending submission was handled
   */
  #resumeAfterSubmit() {
    let email = null;
    try {
      email = sessionStorage.getItem(PENDING_KEY);
      sessionStorage.removeItem(PENDING_KEY);
    } catch {
      return false;
    }
    if (email === null) return false;

    const url = new URL(location.href);
    if (url.searchParams.get('customer_posted') === 'true' || this.form.querySelector('[data-posted]')) {
      url.searchParams.delete('customer_posted');
      if (url.hash === '#SpindleSignupForm') url.hash = '';
      history.replaceState(history.state, '', url);
      this.#open({ focus: false });
      this.#done(email);
      return true;
    }

    if (!this.errorMessage.hidden) {
      this.emailInput.value = email;
      this.#open({ focus: true });
      return true;
    }
    return false;
  }

  /** @param {string} email */
  #done(email) {
    this.#subscribed = true;
    writeState('subscribed');

    for (const title of this.querySelectorAll('[data-title]')) {
      /** @type {HTMLElement} */ (title).hidden = /** @type {HTMLElement} */ (title).dataset.title !== 'done';
    }
    const emailSlot = this.querySelector('[data-email]');
    if (emailSlot) emailSlot.textContent = email;

    const formStep = /** @type {HTMLElement} */ (this.querySelector('[data-step="form"]'));
    const doneStep = /** @type {HTMLElement} */ (this.querySelector('[data-step="done"]'));
    formStep.hidden = true;
    doneStep.hidden = false;
    if (this.tab) this.tab.hidden = true;

    // Tie this browser's visits to the new subscriber, so Klaviyo's browse
    // and cart reminders can reach them before they open an email. Consent
    // still comes from Shopify; this only names the visitor.
    try {
      const klaviyo = /** @type {any} */ (window).klaviyo;
      if (typeof klaviyo?.identify === 'function') {
        klaviyo.identify({ email });
      } else {
        /** @type {any} */ (window)._learnq?.push(['identify', { $email: email }]);
      }
    } catch {
      // Analytics never gets in the way of the confirmation.
    }
  }

  /**
   * In the theme editor, show the panel while its section is selected. The
   * tab stays visible so there is something to click to select it.
   */
  #previewInEditor() {
    const { signal } = this.#lifetime;
    this.#showTab();
    /** @param {Event} event */
    const mine = (event) => /** @type {CustomEvent} */ (event).detail?.sectionId === this.dataset.sectionId;

    document.addEventListener('shopify:section:select', (event) => mine(event) && this.#open({ focus: false }), {
      signal,
    });
    document.addEventListener(
      'shopify:section:deselect',
      (event) => {
        if (!mine(event)) return;
        this.#hide();
        this.#showTab();
      },
      { signal }
    );
    document.addEventListener('shopify:section:load', (event) => mine(event) && this.#open({ focus: false }), {
      signal,
    });
  }

  /**
   * @param {() => void} callback
   * @param {number} delay
   */
  #later(callback, delay) {
    this.#timers.push(window.setTimeout(callback, delay));
  }
}

if (!customElements.get('spindle-signup')) {
  customElements.define('spindle-signup', SpindleSignup);
}
